import { Alert, Box, Button, CircularProgress, FormControl, Grid, InputAdornment, InputLabel, MenuItem, Paper, Radio, Select, Stack, TextField, Typography } from "@mui/material"
import { useState, useReducer, useEffect } from "react"
import { useLocation, useNavigate } from "react-router-dom"
import { z } from 'zod'
import cep from 'cep-promise'

import useInternalFetch from "../../../api/apiInternal"
import InputMask  from 'react-input-mask'
import config from "../../../common/Config"

const SubscriptionForm = () => {
	
	const [inputFields, dispatch]				= useReducer(reducer, { interval : 'year' }) 
	const location								= useLocation()
	const stateList								= ['AC', 'AL', 'AP', 'AM', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA', 'MT', 'MS', 'MG', 'PA', 'PB', 'PR', 'PE', 'PI', 'RJ', 'RN', 'RS', 'RO', 'RR', 'SC', 'SP', 'SE', 'TO']
	
	const [phoneMaskType, setPhoneMaskType]		= useState('(99) 9999-9999')
	const [returnObject, setReturnObject]		= useState()
	const [highlightErrors, setHighlightErrors]	= useState()
	const [isSaving, setIsSaving]				= useState(false)
	const [isSearching, setIsSearching]			= useState(false)

	const internalFetch							= useInternalFetch()
	const navigate								= useNavigate()

	useEffect(() => {
		dispatch({type: 'update', field : 'start_at', payload: location.state?.start_at})
	}, [location.state])

	const handleSubmit = () => {

		if ( inputFields.isValid ){
			setIsSaving(true)

			const options = {
				method : "POST",
				headers : {
					"Content-Type" : "application/json"	
				},
				body : JSON.stringify(inputFields)
			}

			internalFetch(config.apiUrl +'/subscribe', options)
			.then(result => {
				if (result.success) {
					navigate('/subscription', { state: { returnMessage: 'Assinatura realizada com sucesso!' }} )
				}
				else {
					setReturnObject(result)
				}
			})
			.finally(() => setIsSaving(false))
		}
		else {
			setHighlightErrors(true)
		}

	}

	const handleCepSearch = (e) => {
		const currentZipCode = e.target.value.replace(/[^0-9]/,'')
		setIsSearching(true)

		cep(currentZipCode)
		.then(result => {
			if (result.name !== 'CepPromiseError') {
				dispatch({type : 'update', field : 'address', payload: result.street })
				dispatch({type : 'update', field : 'neighborhood', payload: result.neighborhood })
				dispatch({type : 'update', field : 'city', payload: result.city })
				dispatch({type : 'update', field : 'state', payload: result.state })
			}
			setIsSearching(false)
		})
		.catch(e => setIsSearching(false))
	}
	
	return (
		<Grid container>
			{returnObject ? (
				<Grid item xs="12">
					<Alert severity={returnObject.success ? "success" : "error"} onClose={() => setReturnObject(null)} sx={{mt: 2, ml: 2, mr: 2}}>{returnObject.message}</Alert>
				</Grid>
			) : null}
			<Grid item xs="12" md="6" lg="8">
				<Paper elevation={2} sx={{padding: 1, margin: 2}}> 
					<Typography fontWeight={'bold'} paddingBottom={1}>Seus Dados</Typography>
					<Grid container spacing={1}>
						<Grid item xs="6">
							<TextField 
								label="Nome" 
								name="name"
								value={inputFields.name || ''} 
								onChange={(e) => dispatch({type: 'update', field : 'name', payload: e.target.value})}
								error={highlightErrors && inputFields.errors?.name}
								helperText={highlightErrors && inputFields.errors?.name}
								fullWidth			
							/>
						</Grid>
						<Grid item xs="6">
							<TextField 
								label="E-mail" 
								name="email"
								value={inputFields.email || ''} 
								onChange={(e) => dispatch({type: 'update', field : 'email', payload: e.target.value})}
								error={highlightErrors && inputFields.errors?.email}
								helperText={highlightErrors && inputFields.errors?.email}
								fullWidth
							/>	
						</Grid>
						<Grid item xs="6">
							<InputMask 
								mask={'999.999.999-99'}
								maskChar=""
								label="CPF" 
								name="cpf"
								value={inputFields.cpf || ''} 
								onChange={(e) => dispatch({type: 'update', field : 'cpf', payload: e.target.value})}
								error={highlightErrors && inputFields.errors?.cpf}
								helperText={highlightErrors && inputFields.errors?.cpf}
								fullWidth
								>{(inputProps) => 
									<TextField {...inputProps} />
								}
							</InputMask>
						</Grid>
						<Grid item xs="6">
							<InputMask 
								mask={phoneMaskType}
								maskChar=""
								beforeMaskedValueChange ={(newState , oldState , userInput ) => {
									var {value, selection}	= newState

									// Caso o valor seja colado ou preenchido via autofill
									if (userInput?.length >= 11) {
										setPhoneMaskType('(99) 99999-9999')
										return {
											value 		: userInput,
											selection	: {start : selection.start+2, end : selection.end+2}
										}						
									}

									// Caso o usuario esteja digitando celular
									if (userInput && oldState.value.length === 14) {
										setPhoneMaskType('(99) 99999-9999')
										return {
											value 		: value + userInput,
											selection	: {start : selection.start+2, end : selection.end+2}
										}
									}

									// Caso o usuario esteja apagando celular e voltando para fixo
									if (!userInput && value.length <= 14) {
										setPhoneMaskType('(99) 9999-9999')
									}

									return newState
								}}
								label="Telefone" 
								name="phone"
								value={inputFields.phone || ''} 
								onChange={(e) => dispatch({type: 'update', field : 'phone', payload: e.target.value})}
								error={highlightErrors && inputFields.errors?.phone}
								helperText={highlightErrors && inputFields.errors?.phone}
								fullWidth
								>{(inputProps) => 
									<TextField {...inputProps} />
								}
							</InputMask>				
						</Grid>
						
						<Grid item xs="4">
							<InputMask 
								mask={'99999-999'}
								maskChar=""
								label="CEP" 
								name="zipCode"
								value={inputFields.zipCode || ''} 
								onChange={(e) => dispatch({type: 'update', field : 'zipCode', payload: e.target.value})}
								onBlur={handleCepSearch}
								error={highlightErrors && inputFields.errors?.zipCode}
								helperText={highlightErrors && inputFields.errors?.zipCode}
								fullWidth
								>{(inputProps) => 
									<TextField {...inputProps} />
								}
							</InputMask>	
						</Grid>
						<Grid item xs="8">
							<TextField 
								label="Endereço" 
								name="address"
								value={inputFields.address || ''} 
								onChange={(e) => dispatch({type: 'update', field : 'address', payload: e.target.value})}
								error={highlightErrors && inputFields.errors?.address}
								helperText={highlightErrors && inputFields.errors?.address}
								disabled={isSearching}
								InputProps={{
									endAdornment: isSearching ? (
									<InputAdornment position="end">
										<CircularProgress size={20} />
									</InputAdornment>
									) : null,
								}}															
								fullWidth
							/>	
						</Grid>
						<Grid item xs="6">
							<TextField 
								label="Número" 
								name="addressNumber"
								value={inputFields.addressNumber || ''} 
								onChange={(e) => dispatch({type: 'update', field : 'addressNumber', payload: e.target.value})}
								error={highlightErrors && inputFields.errors?.addressNumber}
								helperText={highlightErrors && inputFields.errors?.addressNumber}
								fullWidth
							/>					
						</Grid>			
						<Grid item xs="6">
							<TextField 
								label="Complemento" 
								name="addressOther"
								value={inputFields.addressOther || ''} 
								onChange={(e) => dispatch({type: 'update', field : 'addressOther', payload: e.target.value})}
								error={highlightErrors && inputFields.errors?.addressOther}
								helperText={highlightErrors && inputFields.errors?.addressOther}
								fullWidth					
							/>				
						</Grid>		
						<Grid item xs="5">
							<TextField 
								label="Bairro" 
								name="neighborhood"
								value={inputFields.neighborhood || ''} 
								onChange={(e) => dispatch({type: 'update', field : 'neighborhood', payload: e.target.value})}
								error={highlightErrors && inputFields.errors?.neighborhood}
								helperText={highlightErrors && inputFields.errors?.neighborhood}
								disabled={isSearching}
								InputProps={{
									endAdornment: isSearching ? (
									<InputAdornment position="end">
										<CircularProgress size={20} />
									</InputAdornment>
									) : null,
								}}		
								fullWidth
							/>					
						</Grid>
						<Grid item xs="5">
							<TextField 
								label="Cidade" 
								name="city"
								value={inputFields.city || ''} 
								onChange={(e) => dispatch({type: 'update', field : 'city', payload: e.target.value})}
								error={highlightErrors && inputFields.errors?.city}
								helperText={highlightErrors && inputFields.errors?.city}
								disabled={isSearching}
								InputProps={{
									endAdornment: isSearching ? (
									<InputAdornment position="end">
										<CircularProgress size={20} />
									</InputAdornment>
									) : null,
								}}		
								fullWidth
							/>	
						</Grid>
						<Grid item xs="2">
							<FormControl fullWidth>
								<InputLabel id="state-label">Estado</InputLabel>
								<Select
									labelId="state-label"
									value={inputFields.state || ''}
									label="Estado"
									onChange={(e) => dispatch({type: 'update', field : 'state', payload: e.target.value})}
									error={highlightErrors && inputFields.errors?.state}
									helperText={highlightErrors && inputFields.errors?.state}
									MenuProps={{
										PaperProps: {sx: {maxHeight: '300px'}},
									}}									
									disabled={isSearching}
									endAdornment={
										isSearching ? (
											<InputAdornment sx={{margin: 3}}>
												<CircularProgress size={20} />
											</InputAdornment>
										) : null
									}									
									>
									{stateList.map(s => (<MenuItem value={s}>{s}</MenuItem>))}
								</Select>
							</FormControl>							
						</Grid>
					</Grid>
				</Paper>
					
				<Paper elevation={2} sx={{padding: 1, margin: 2}}> 
					<Typography fontWeight={'bold'} paddingBottom={1} color={'#303030'}>Dados do seu Cartão</Typography>
					<Grid container spacing={1}>
						<Grid item xs="12">
							<TextField 
								label="Nome no Cartao" 
								name="cardHolder"
								value={inputFields.cardHolder || ''} 
								onChange={(e) => dispatch({type: 'update', field : 'cardHolder', payload: e.target.value})}
								error={highlightErrors && inputFields.errors?.cardHolder}
								helperText={highlightErrors && inputFields.errors?.cardHolder}
								fullWidth
							/>	
						</Grid>
						<Grid item xs="12">
							<TextField 
								label="Numero do Cartao" 
								name="cardNumber"
								value={inputFields.cardNumber || ''} 
								onChange={(e) => dispatch({type: 'update', field : 'cardNumber', payload: e.target.value})}
								error={highlightErrors && inputFields.errors?.cardNumber}
								helperText={highlightErrors && inputFields.errors?.cardNumber}
								fullWidth
							/>	
						</Grid>
						<Grid item xs="6">
							<InputMask
								mask='99/99'
								value={inputFields.cardExpireDate || ''}
								disabled={false}
								onChange={(e) => dispatch({type: 'update', field : 'cardExpireDate', payload: e.target.value})}		
								label="Vencimento" 
								name="cardExpireDate"
								error={highlightErrors && inputFields.errors?.cardExpireDate}
								helperText={highlightErrors && inputFields.errors?.cardExpireDate}
								fullWidth
								>{(inputProps) => 
									<TextField {...inputProps}/>
								}
							</InputMask>
						</Grid>
						<Grid item xs="6">
							<TextField 
								label="CVV" 
								name="cvv"
								value={inputFields.cvv || ''} 
								onChange={(e) => dispatch({type: 'update', field : 'cvv', payload: e.target.value})}
								error={highlightErrors && inputFields.errors?.cvv}
								helperText={highlightErrors && inputFields.errors?.cvv}
								fullWidth
							/>
						</Grid>
					</Grid>
				</Paper>
				
			</Grid>
			<Grid item xs="12" md="6" lg="4">
				<Paper elevation={2} sx={{padding: 2, margin: 2, height:'90%'}}>
					<Stack direction={'column'} spacing={1} alignItems={'center'} justifyContent={'space-between'} height={'100%'} >
						
						<Box width={'100%'}>
							<Typography variant="h4" fontWeight="bold" color={'#37393b'}>SEJA MEMBRO</Typography>
							<Typography variant="h6" color={'#303030'}>Escolha seu Plano</Typography>
						</Box>

						<Stack width={'100%'} spacing={1}>
							<Box sx={{display: 'flex', flexDirection: 'column', height: 150, justifyContent: 'center', borderRadius: 2, backgroundColor: '#b7c0ed', width: '100%'}}> 
								<Box sx={{display: 'flex', alignItems: 'center', flexGrow: 1}}>
									<Radio 
										checked={inputFields.interval === 'year'}
										onChange={() => dispatch({type: 'update', field : 'interval', payload: 'year'})}
										value={'year'}
										name="radio-interval"
									/>
									<Typography variant='h6' flexGrow={1}>Anual</Typography>
									<Stack direction={'column'} paddingRight={1}>
										<Typography sx={{textAlign: 'right',textDecoration: 'line-through'}}>R$ 1069</Typography>
										<Typography variant='h5' fontWeight={'bold'}>R$ 590</Typography>
									</Stack>								
								</Box>
								<Typography m={1}>Ganhe dois meses grátis!</Typography>
							</Box>
							<Box sx={{display: 'flex', flexDirection: 'column', height: 150, justifyContent: 'center', borderRadius: 2, backgroundColor: '#b7c0ed', width: '100%'}}> 
								<Box sx={{display: 'flex', alignItems: 'center'}}>
									<Radio
										checked={inputFields.interval === 'month'}
										onChange={() => dispatch({type: 'update', field : 'interval', payload: 'month'})}
										value={'month'}
										name="radio-interval"
									/>
									<Typography variant='h6' flexGrow={1}>Mensal</Typography>
									<Stack direction={'column'} paddingRight={1}>
										<Typography sx={{textAlign: 'right',textDecoration: 'line-through'}}>R$ 89</Typography>
										<Typography variant='h5' fontWeight={'bold'}>R$ 59</Typography>
									</Stack>	
								</Box>
							</Box>
						</Stack>

						<Button variant="contained" type="submit" sx={{height: 75, fontSize: '1rem', fontWeight: 'bold'}} onClick={handleSubmit} disabled={isSaving}>Virar Membro Agora!</Button>
					</Stack>
				</Paper>
			</Grid>
		</Grid>		
	)
}

const reducer = (inputFields, action) => {
	let updatedFields = inputFields

	switch (action.type) {

		case 'update' : {
			updatedFields = {
				...updatedFields,
				[action.field] : action.payload
			}
		}

	}

	const zodParseResults = inputFieldsSchema.safeParse(updatedFields)

	updatedFields.isValid 	= zodParseResults.success
	updatedFields.errors	= parseZodErrors(zodParseResults)

	return updatedFields
}

const inputFieldsSchema = z.object({
	name 				: z.string({required_error: 'Preencha o nome'}).trim().min(1, {message: 'Nome inválido!'}),
	email				: z.string({required_error: 'Preencha o e-mail'}).email({message: 'E-mail inválido'}),
	cpf					: z.string({required_error: 'Preencha o CPF'}).regex(/\d{3}\.\d{3}\.\d{3}-\d{2}/, {message: 'CPF inválido'}),
	phone				: z.string({required_error: 'Preencha o telefone'}).regex(/^\(\d{2}\) [\d-]{9,10}$/, {message: 'Telefone inválido'}),
	zipCode				: z.string({required_error: 'Preencha o CEP'}).regex(/^\d{5}-\d{3}$/, 'CEP Inválido'),
	address				: z.string({required_error: 'Preencha o endereço.'}),
	addressNumber		: z.string({required_error: 'Preencha o número do endereço.'}),
	neighborhood		: z.string({required_error: 'Preencha o bairro.'}),
	city				: z.string({required_error: 'Preencha a cidade.'}),
	state				: z.string({required_error: 'Selecione o estado.'}),
	cardHolder			: z.string({required_error: 'Preencha o nome do titular do cartão'}),
	cardNumber			: z.string({required_error: 'Preencha o número do cartão'}).regex(/^\d{13,19}$/, {message : 'Número do cartão inválido'}),
	cardExpireDate	 	: z.string({required_error: 'Preencha a validade do cartão'}).regex(/^\d{2}\/\d{2}$/, {message: 'Data da validade inválida'}),
	cvv					: z.string({required_error: 'Preencha o CVV do cartão'}).regex(/^\d{3,4}$/, {message: 'CVV inválido'})
})

const parseZodErrors = (pErrors) => {
	return pErrors.error?.issues?.reduce( (errors, e) => ({...errors, [e.path[0]] : e.message}), {})
}

export default SubscriptionForm